import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Kpi";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Pencil, Plus, X, Mail } from "lucide-react";

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "user", phone: "", send_welcome: true });
    const [editing, setEditing] = useState(null);
    const [sendingWelcome, setSendingWelcome] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/users")
            .then((r) => setUsers(r.data.items || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e?.preventDefault();
        try {
            const payload = { ...newUser };
            if (!payload.password) delete payload.password; // let backend generate temp
            const r = await api.post("/users", payload);
            if (r.data?.welcome_email === "sent") {
                toast.success(`User created — welcome email sent to ${r.data.email}`);
            } else if (r.data?.welcome_email === "resend_not_configured") {
                toast.info("User created — configure Resend in Settings to auto-send welcome emails");
            } else if (r.data?.welcome_email && r.data.welcome_email.startsWith("failed")) {
                toast.warning(`User created but welcome email failed: ${r.data.welcome_email}`);
            } else {
                toast.success("User created");
            }
            setNewUser({ email: "", name: "", password: "", role: "user", phone: "", send_welcome: true });
            setAdding(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const sendWelcome = async (u) => {
        if (!window.confirm(`Send a welcome email + password-setup link to ${u.email}? This will reset their current password.`)) return;
        setSendingWelcome(u.id);
        try {
            const r = await api.post(`/account/send-welcome/${u.id}`);
            toast.success(`Welcome email sent to ${r.data.sent_to}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSendingWelcome(null);
        }
    };

    const saveEdit = async (e) => {
        e?.preventDefault();
        try {
            const payload = { name: editing.name, role: editing.role, phone: editing.phone };
            if (editing.new_password) payload.password = editing.new_password;
            await api.patch(`/users/${editing.id}`, payload);
            toast.success("User updated");
            setEditing(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div>
            <PageHeader title="Users" subtitle="Manage who can access Takealot Insight">
                <button
                    data-testid="add-user-btn"
                    onClick={() => setAdding(!adding)}
                    className="inline-flex items-center gap-1.5 bg-[color:var(--bl-takealot-blue)] text-white px-3 py-1.5 text-sm rounded-sm hover:brightness-110"
                >
                    {adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    {adding ? "Cancel" : "Add user"}
                </button>
            </PageHeader>

            {adding && (
                <form
                    onSubmit={create}
                    data-testid="add-user-form"
                    className="mb-6 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm"
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <input
                            data-testid="new-user-email"
                            type="email" required placeholder="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                        />
                        <input
                            data-testid="new-user-name"
                            type="text" placeholder="name"
                            value={newUser.name}
                            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                            className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                        />
                        <input
                            data-testid="new-user-phone"
                            type="tel" placeholder="phone (optional)"
                            value={newUser.phone}
                            onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                            className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                        />
                        <input
                            data-testid="new-user-password"
                            type="password" minLength={6} placeholder="password (blank = auto-generate)"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                        />
                        <select
                            data-testid="new-user-role"
                            value={newUser.role}
                            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                            className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                        >
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                        </select>
                        <button data-testid="create-user-submit" type="submit" className="bg-[color:var(--bl-takealot-blue)] text-white px-3 py-2 text-sm rounded-sm hover:brightness-110">
                            Create
                        </button>
                    </div>
                    <label className="flex items-center gap-2 mt-3 text-xs text-[color:var(--bl-text-dim)]">
                        <input data-testid="new-user-send-welcome" type="checkbox" checked={newUser.send_welcome} onChange={(e) => setNewUser({ ...newUser, send_welcome: e.target.checked })} />
                        Send a welcome email with a password-setup link (requires Resend to be configured)
                    </label>
                </form>
            )}

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg-2)]/40">
                        <tr>
                            <th className="text-left px-4 py-2">Email</th>
                            <th className="text-left px-3 py-2">Name</th>
                            <th className="text-left px-3 py-2">Role</th>
                            <th className="text-left px-3 py-2">Created</th>
                            <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody data-testid="users-table">
                        {loading && (<tr><td colSpan={5} className="text-center text-[color:var(--bl-text-dim)] py-6">Loading…</td></tr>)}
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/50">
                                <td className="px-4 py-3">{u.email}</td>
                                <td className="px-3 py-3">{u.name}</td>
                                <td className="px-3 py-3">
                                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm ${
                                        u.role === "admin"
                                            ? "bg-[color:var(--bl-takealot-orange)]/15 text-[color:var(--bl-takealot-orange)]"
                                            : "bg-[color:var(--bl-takealot-blue)]/15 text-[color:var(--bl-takealot-blue)]"
                                    }`}>{u.role}</span>
                                </td>
                                <td className="px-3 py-3 font-mono-num text-xs text-[color:var(--bl-text-dim)]">{(u.created_at || "").slice(0, 10)}</td>
                                <td className="px-3 py-3 text-right whitespace-nowrap">
                                    <button
                                        data-testid={`send-welcome-${u.email}`}
                                        onClick={() => sendWelcome(u)}
                                        disabled={sendingWelcome === u.id}
                                        title="Send welcome email + password setup link (resets current password)"
                                        className="inline-flex items-center gap-1 text-xs text-[color:var(--bl-takealot-orange)] hover:underline mr-3 disabled:opacity-50"
                                    ><Mail className="w-3 h-3" /> {sendingWelcome === u.id ? "Sending…" : "Welcome"}</button>
                                    <button
                                        data-testid={`edit-user-${u.email}`}
                                        onClick={() => setEditing({ ...u, new_password: "" })}
                                        className="inline-flex items-center gap-1 text-xs text-[color:var(--bl-takealot-blue)] hover:underline mr-3"
                                    ><Pencil className="w-3 h-3" /> Edit</button>
                                    {u.email !== me?.email && (
                                        <button
                                            data-testid={`delete-user-${u.email}`}
                                            onClick={() => remove(u)}
                                            className="inline-flex items-center gap-1 text-xs text-[color:var(--bl-danger)] hover:underline"
                                        ><Trash2 className="w-3 h-3" /> Delete</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
                    <form
                        onSubmit={saveEdit}
                        data-testid="edit-user-modal"
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm p-6 shadow-xl"
                    >
                        <h3 className="font-serif-display text-2xl mb-1">Edit user</h3>
                        <p className="text-xs text-[color:var(--bl-text-dim)] mb-4 font-mono-num">{editing.email}</p>

                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Name</label>
                        <input data-testid="edit-name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            className="w-full mb-3 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />

                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Role</label>
                        <select data-testid="edit-role" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                            className="w-full mb-3 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]">
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                        </select>

                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Phone (for "Forgot your email?" lookup)</label>
                        <input data-testid="edit-phone" type="tel" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                            className="w-full mb-3 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />

                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">New password (optional)</label>
                        <input data-testid="edit-password" type="password" placeholder="leave blank to keep current"
                            value={editing.new_password || ""} onChange={(e) => setEditing({ ...editing, new_password: e.target.value })}
                            className="w-full mb-5 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />

                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm border border-[color:var(--bl-border)] rounded-sm">Cancel</button>
                            <button data-testid="save-edit-user" type="submit" className="px-3 py-1.5 text-sm bg-[color:var(--bl-takealot-blue)] text-white rounded-sm hover:brightness-110">Save</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
